import {z} from 'zod';

/**
 * Zod schemas for PebblePlot JSON specification
 * Similar to Plotly's reference structure
 */

// Number line interval definition schema (line segment or point)
// If end is provided, renders as a line segment from start to end
// If end is not provided, renders as a single point at start
const pebbleplotNumberLineIntervalSchema = z
  .object({
    type: z.literal('interval'),
    start: z.number(),
    end: z.number().optional(),
    startOpen: z.boolean(),
    endOpen: z.boolean().optional(),
    label: z.string().optional(),
    color: z.string().optional(),
    offset: z.number().optional(), // Vertical offset in pixels from the number line (positive = above, negative = below)
  })
  .refine(
    (data) => {
      // If end is provided, it must be greater than start
      if (data.end !== undefined) {
        return data.start < data.end;
      }
      return true;
    },
    {
      message: 'end must be greater than start when provided',
      path: ['end'],
    },
  )
  .refine(
    (data) => {
      // If end is provided, endOpen must also be provided
      if (data.end !== undefined && data.endOpen === undefined) {
        return false;
      }
      return true;
    },
    {
      message: 'endOpen is required when end is provided',
      path: ['endOpen'],
    },
  );

// Number line graph schema
export const pebbleplotNumberLineSchema = z.object({
  type: z.literal('numberline'),
  range: z.tuple([z.number(), z.number()]).refine(([min, max]) => min < max, {
    message: 'range[0] must be less than range[1]',
  }),
  lines: z.array(pebbleplotNumberLineIntervalSchema).optional().default([]),
});

export type PebblePlotNumberLine = z.infer<typeof pebbleplotNumberLineSchema>;
export type PebblePlotNumberLineInterval = z.infer<typeof pebbleplotNumberLineIntervalSchema>;
